import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type UpdateData,
} from 'firebase/firestore';
import { db } from './firebase';
import { adminScopeService } from './adminScopeService';

export interface MenuIngredient {
  id: string;
  label: string;
  normalizedName: string;
  aliases: string[];
  storeId?: string | null;
  removableDefault: boolean;
  active: boolean;
  sortOrder: number;
}

const COLLECTION = 'menuIngredients';
const PRODUCTS_COLLECTION = 'products';
const MIGRATION_VERSION = 1;

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const compactNormalize = (value: string) => normalize(value).replace(/_/g, '');

const toModel = (id: string, data: any): MenuIngredient => ({
  id,
  label: String(data.label ?? data.name ?? ''),
  normalizedName: String(data.normalizedName ?? normalize(String(data.label ?? data.name ?? ''))),
  aliases: Array.isArray(data.aliases) ? data.aliases.map((item: unknown) => String(item)).filter(Boolean) : [],
  storeId: data.storeId ?? null,
  removableDefault: data.removableDefault !== false,
  active: data.active !== false && data.isActive !== false,
  sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
});

export interface MenuIngredientMigrationCandidate {
  label: string;
  normalizedName: string;
  productCount: number;
}

export interface MenuIngredientMigrationProduct {
  productId: string;
  productName: string;
  legacyLabels: string[];
  existingMappingCount: number;
  mappingsToCreate: number;
  ignoredReason?: string;
}

export interface MenuIngredientMigrationPreview {
  generatedAt: number;
  storeId: string;
  totalProducts: number;
  productsWithLegacyIngredients: number;
  productsAlreadyFullyMapped: number;
  ignoredProducts: number;
  menuIngredientsToCreate: MenuIngredientMigrationCandidate[];
  productMappingsToCreate: number;
  products: MenuIngredientMigrationProduct[];
}

export interface MenuIngredientMigrationApplyResult {
  generatedAt: number;
  createdMenuIngredients: number;
  updatedProducts: number;
  skippedProducts: number;
  remainingPreview: MenuIngredientMigrationPreview;
}

export type MenuIngredientMigrationDecisionAction = 'USE' | 'EXCLUDE';

export interface MenuIngredientMigrationDecision {
  normalizedName: string;
  action: MenuIngredientMigrationDecisionAction;
  targetLabel?: string;
}

interface LegacyLabel {
  label: string;
  normalizedName: string;
  menuIngredientId?: string;
  removable: boolean;
  isDefaultIncluded: boolean;
  sortOrder: number;
}

interface ProductMigrationSource {
  id: string;
  name: string;
  category: string;
  compositionMode?: string;
  data: Record<string, any>;
  labels: LegacyLabel[];
  ignoredReason?: string;
}

const cleanLabel = (value: unknown): string | null => {
  const label = String(value ?? '')
    .trim()
    .replace(/^"+|"+$/g, '')
    .replace(/\s+/g, ' ');
  const lower = label.toLowerCase();
  return label && lower !== 'null' && lower !== '[object object]' ? label : null;
};

const parseStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string') return cleanLabel(item);
      if (item && typeof item === 'object') {
        return cleanLabel((item as any).label ?? (item as any).name ?? (item as any).displayName ?? (item as any).ingredientName);
      }
      return cleanLabel(item);
    }).filter((item): item is string => Boolean(item));
  }
  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }
  try {
    const decoded = JSON.parse(value);
    if (Array.isArray(decoded)) {
      return decoded.map((item) => {
        if (typeof item === 'string') return cleanLabel(item);
        if (item && typeof item === 'object') {
          return cleanLabel((item as any).label ?? (item as any).name ?? (item as any).displayName);
        }
        return null;
      }).filter((item): item is string => Boolean(item));
    }
  } catch {
    // Fallback to separator parsing below.
  }
  return value.split(/[,;\n]/).map(cleanLabel).filter((item): item is string => Boolean(item));
};

const parseDisplayIngredients = (data: Record<string, any>): LegacyLabel[] => {
  const raw = Array.isArray(data.displayIngredients)
    ? data.displayIngredients
    : Array.isArray(data.menuIngredients)
      ? data.menuIngredients
      : [];

  const labels = raw.map((item: any, index: number): LegacyLabel | null => {
    if (typeof item === 'string') {
      const label = cleanLabel(item);
      if (!label) return null;
      return {
        label,
        normalizedName: normalize(label),
        removable: true,
        isDefaultIncluded: true,
        sortOrder: index,
      };
    }
    if (!item || typeof item !== 'object') return null;
    const label = cleanLabel(item.label ?? item.name ?? item.displayName ?? item.ingredientName);
    if (!label) return null;
    const menuIngredientId = cleanLabel(item.menuIngredientId ?? item.id ?? item.ingredientId) ?? undefined;
    return {
      label,
      normalizedName: normalize(label),
      menuIngredientId,
      removable: item.removable !== false,
      isDefaultIncluded: item.isDefaultIncluded !== false,
      sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : index,
    };
  }).filter((item): item is LegacyLabel => Boolean(item?.normalizedName));

  if (labels.length > 0) {
    return dedupeLabels(labels);
  }

  return dedupeLabels(parseStringList(data.ingredients).map((label, index) => ({
    label,
    normalizedName: normalize(label),
    removable: true,
    isDefaultIncluded: true,
    sortOrder: index,
  })).filter((item) => Boolean(item.normalizedName)));
};

const dedupeLabels = (labels: LegacyLabel[]): LegacyLabel[] => {
  const seen = new Set<string>();
  return labels
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((item) => {
      if (seen.has(item.normalizedName)) return false;
      seen.add(item.normalizedName);
      return true;
    })
    .map((item, index) => ({ ...item, sortOrder: index }));
};

const shouldIgnoreProduct = (productName: string, compositionMode: unknown, labels: LegacyLabel[]): string | undefined => {
  if (String(compositionMode ?? '').toUpperCase() === 'COMPOSITE') {
    return undefined;
  }
  if (labels.length === 1 && compactNormalize(productName) === compactNormalize(labels[0].label)) {
    return 'Produit simple: l’ancien ingrédient est identique au nom du produit.';
  }
  return undefined;
};

const buildProductSources = async (storeId: string): Promise<ProductMigrationSource[]> => {
  const productsSnapshot = await getDocs(query(collection(db, PRODUCTS_COLLECTION), where('storeId', '==', storeId)));
  return productsSnapshot.docs.map((productDoc) => {
    const data = productDoc.data();
    const labels = parseDisplayIngredients(data);
    const name = cleanLabel(data.name) ?? '[Sans nom]';
    return {
      id: productDoc.id,
      name,
      category: cleanLabel(data.categoryId ?? data.categoryKey ?? data.category) ?? '',
      compositionMode: typeof data.compositionMode === 'string' ? data.compositionMode : undefined,
      data,
      labels,
      ignoredReason: shouldIgnoreProduct(name, data.compositionMode, labels),
    };
  });
};

const getScopedMenuIngredients = async (storeId: string): Promise<MenuIngredient[]> => {
  const snapshot = await getDocs(query(collection(db, COLLECTION), where('storeId', '==', storeId)));
  return snapshot.docs.map((item) => toModel(item.id, item.data()));
};

const assertStoreScope = (): string => {
  const currentStoreId = adminScopeService.getCurrentStoreId();
  if (!currentStoreId) {
    throw new Error('Sélectionnez un magasin avant de lancer la migration des ingrédients menu.');
  }
  return currentStoreId;
};

const buildMigrationPreview = (
  storeId: string,
  products: ProductMigrationSource[],
  menuIngredients: MenuIngredient[]
): MenuIngredientMigrationPreview => {
  const menuByNormalized = new Map(menuIngredients.map((item) => [item.normalizedName, item]));
  const menuIds = new Set(menuIngredients.map((item) => item.id));
  const candidateCounts = new Map<string, { label: string; count: number }>();

  const productRows: MenuIngredientMigrationProduct[] = products
    .filter((product) => product.labels.length > 0)
    .map((product) => {
      if (product.ignoredReason) {
        return {
          productId: product.id,
          productName: product.name,
          legacyLabels: product.labels.map((item) => item.label),
          existingMappingCount: 0,
          mappingsToCreate: 0,
          ignoredReason: product.ignoredReason,
        };
      }

      let existingMappingCount = 0;
      let mappingsToCreate = 0;
      product.labels.forEach((label) => {
        const matchingMenu = menuByNormalized.get(label.normalizedName);
        if (!matchingMenu) {
          const current = candidateCounts.get(label.normalizedName);
          candidateCounts.set(label.normalizedName, {
            label: current?.label ?? label.label,
            count: (current?.count ?? 0) + 1,
          });
          mappingsToCreate += 1;
          return;
        }
        if (label.menuIngredientId && menuIds.has(label.menuIngredientId) && label.menuIngredientId === matchingMenu.id) {
          existingMappingCount += 1;
        } else {
          mappingsToCreate += 1;
        }
      });

      return {
        productId: product.id,
        productName: product.name,
        legacyLabels: product.labels.map((item) => item.label),
        existingMappingCount,
        mappingsToCreate,
      };
    });

  const activeRows = productRows.filter((product) => !product.ignoredReason);
  const menuIngredientsToCreate = Array.from(candidateCounts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([normalizedName, value]) => ({
      label: value.label,
      normalizedName,
      productCount: value.count,
    }));

  return {
    generatedAt: Date.now(),
    storeId,
    totalProducts: products.length,
    productsWithLegacyIngredients: activeRows.length,
    productsAlreadyFullyMapped: activeRows.filter((product) => product.mappingsToCreate === 0).length,
    ignoredProducts: productRows.filter((product) => product.ignoredReason).length,
    menuIngredientsToCreate,
    productMappingsToCreate: activeRows.reduce((sum, product) => sum + product.mappingsToCreate, 0),
    products: productRows.sort((a, b) => b.mappingsToCreate - a.mappingsToCreate || a.productName.localeCompare(b.productName)),
  };
};

const commitInChunks = async (mutations: Array<(batch: ReturnType<typeof writeBatch>) => void>) => {
  const chunkSize = 450;
  for (let index = 0; index < mutations.length; index += chunkSize) {
    const batch = writeBatch(db);
    mutations.slice(index, index + chunkSize).forEach((mutation) => mutation(batch));
    await batch.commit();
  }
};

const buildDecisionMap = (decisions: MenuIngredientMigrationDecision[] = []) => {
  const map = new Map<string, MenuIngredientMigrationDecision>();
  decisions.forEach((decision) => {
    const normalizedName = normalize(decision.normalizedName);
    if (!normalizedName) return;
    map.set(normalizedName, {
      normalizedName,
      action: decision.action === 'EXCLUDE' ? 'EXCLUDE' : 'USE',
      targetLabel: cleanLabel(decision.targetLabel) ?? undefined,
    });
  });
  return map;
};

export const menuIngredientService = {
  async list(activeOnly = true): Promise<MenuIngredient[]> {
    const currentStoreId = adminScopeService.getCurrentStoreId();
    const ref = collection(db, COLLECTION);
    const snapshot = currentStoreId
      ? await getDocs(query(ref, where('storeId', '==', currentStoreId)))
      : await getDocs(query(ref, orderBy('sortOrder', 'asc')));
    return snapshot.docs
      .map((item) => toModel(item.id, item.data()))
      .filter((item) => !activeOnly || item.active)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
  },

  async create(label: string, aliases: string[] = []): Promise<MenuIngredient> {
    const trimmed = label.trim();
    if (!trimmed) throw new Error('Ingredient menu vide.');
    const currentStoreId = adminScopeService.getCurrentStoreId();
    const normalizedName = normalize(trimmed);
    const payload = {
      label: trimmed,
      name: trimmed,
      normalizedName,
      globalKey: normalizedName,
      aliases: aliases.map((item) => item.trim()).filter(Boolean),
      storeId: currentStoreId ?? null,
      removableDefault: true,
      active: true,
      sortOrder: Date.now(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const created = await addDoc(collection(db, COLLECTION), payload);
    return toModel(created.id, payload);
  },

  async update(id: string, data: Partial<Pick<MenuIngredient, 'label' | 'aliases' | 'removableDefault' | 'active' | 'sortOrder'>>) {
    const payload: UpdateData<Record<string, any>> = { ...data, updatedAt: serverTimestamp() };
    if (typeof data.label === 'string') {
      payload.name = data.label;
      payload.normalizedName = normalize(data.label);
      payload.globalKey = normalize(data.label);
    }
    if (Array.isArray(data.aliases)) {
      payload.aliases = data.aliases.map((item) => item.trim()).filter(Boolean);
    }
    await updateDoc(doc(db, COLLECTION, id), payload);
  },

  async previewLegacyImport(): Promise<MenuIngredientMigrationPreview> {
    const storeId = assertStoreScope();
    const [products, menuIngredients] = await Promise.all([
      buildProductSources(storeId),
      getScopedMenuIngredients(storeId),
    ]);
    return buildMigrationPreview(storeId, products, menuIngredients);
  },

  async applyLegacyImport(decisions: MenuIngredientMigrationDecision[] = []): Promise<MenuIngredientMigrationApplyResult> {
    const storeId = assertStoreScope();
    const [products, initialMenuIngredients] = await Promise.all([
      buildProductSources(storeId),
      getScopedMenuIngredients(storeId),
    ]);

    const menuByNormalized = new Map(initialMenuIngredients.map((item) => [item.normalizedName, item]));
    const decisionsByNormalized = buildDecisionMap(decisions);
    let nextSortOrder = (initialMenuIngredients.reduce((max, item) => Math.max(max, item.sortOrder), 0) || 0) + 1;
    const createdMenuIngredients: MenuIngredient[] = [];
    const menuMutations: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
    const labelsByNormalized = new Map<string, LegacyLabel>();

    products
      .filter((product) => !product.ignoredReason)
      .flatMap((product) => product.labels)
      .forEach((label) => {
        if (!labelsByNormalized.has(label.normalizedName)) {
          labelsByNormalized.set(label.normalizedName, label);
        }
      });

    Array.from(labelsByNormalized.values())
      .sort((left, right) => left.normalizedName.localeCompare(right.normalizedName))
      .forEach((label) => {
        const decision = decisionsByNormalized.get(label.normalizedName);
        if (decision?.action === 'EXCLUDE') return;
        const targetLabel = cleanLabel(decision?.targetLabel) ?? label.label;
        const targetNormalizedName = normalize(targetLabel);
        if (!targetNormalizedName || menuByNormalized.has(targetNormalizedName)) return;

        const ref = doc(collection(db, COLLECTION));
        const item: MenuIngredient = {
          id: ref.id,
          label: targetLabel,
          normalizedName: targetNormalizedName,
          aliases: [],
          storeId,
          removableDefault: true,
          active: true,
          sortOrder: nextSortOrder++,
        };
        menuByNormalized.set(targetNormalizedName, item);
        createdMenuIngredients.push(item);
        menuMutations.push((batch) => {
          batch.set(ref, {
            label: item.label,
            name: item.label,
            normalizedName: item.normalizedName,
            globalKey: item.normalizedName,
            aliases: [],
            storeId,
            removableDefault: true,
            active: true,
            sortOrder: item.sortOrder,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            source: 'LEGACY_PRODUCT_IMPORT',
            migrationVersion: MIGRATION_VERSION,
          });
        });
      });

    await commitInChunks(menuMutations);

    let updatedProducts = 0;
    let skippedProducts = 0;
    const productMutations: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];

    products.forEach((product) => {
      if (product.labels.length === 0 || product.ignoredReason) {
        if (product.ignoredReason) skippedProducts += 1;
        return;
      }

      const seenTargets = new Set<string>();
      const nextDisplayIngredients = product.labels
        .map((label) => {
          const decision = decisionsByNormalized.get(label.normalizedName);
          if (decision?.action === 'EXCLUDE') return null;
          const targetLabel = cleanLabel(decision?.targetLabel) ?? label.label;
          const targetNormalizedName = normalize(targetLabel);
          if (!targetNormalizedName) return null;
          const menuIngredient = menuByNormalized.get(targetNormalizedName);
          if (!menuIngredient) return null;
          return {
            menuIngredientId: menuIngredient.id,
            label: menuIngredient.label,
            removable: label.removable,
            isDefaultIncluded: label.isDefaultIncluded,
            sortOrder: label.sortOrder,
          };
        })
        .filter((item): item is {
          menuIngredientId: string;
          label: string;
          removable: boolean;
          isDefaultIncluded: boolean;
          sortOrder: number;
        } => Boolean(item))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .filter((item) => {
          if (seenTargets.has(item.menuIngredientId)) return false;
          seenTargets.add(item.menuIngredientId);
          return true;
        })
        .map((item, index) => ({ ...item, sortOrder: index }));

      const currentDisplay = JSON.stringify(parseDisplayIngredients(product.data).map((label, index) => ({
        menuIngredientId: label.menuIngredientId ?? label.normalizedName,
        label: label.label,
        removable: label.removable,
        isDefaultIncluded: label.isDefaultIncluded,
        sortOrder: index,
      })));
      const nextDisplay = JSON.stringify(nextDisplayIngredients);
      const nextIngredients = nextDisplayIngredients.map((item) => item.label);
      const currentIngredients = JSON.stringify(parseStringList(product.data.ingredients));
      const nextCompositionMode = nextDisplayIngredients.length > 0 ? 'COMPOSITE' : 'SIMPLE';

      if (
        currentDisplay === nextDisplay &&
        currentIngredients === JSON.stringify(nextIngredients) &&
        product.data.compositionMode === nextCompositionMode
      ) {
        return;
      }

      updatedProducts += 1;
      productMutations.push((batch) => {
        batch.update(doc(db, PRODUCTS_COLLECTION, product.id), {
          compositionMode: nextCompositionMode,
          ingredients: nextIngredients,
          displayIngredients: nextDisplayIngredients,
          menuIngredientsMigratedAt: serverTimestamp(),
          menuIngredientsMigrationVersion: MIGRATION_VERSION,
          updatedAt: serverTimestamp(),
        });
      });
    });

    await commitInChunks(productMutations);

    return {
      generatedAt: Date.now(),
      createdMenuIngredients: createdMenuIngredients.length,
      updatedProducts,
      skippedProducts,
      remainingPreview: await menuIngredientService.previewLegacyImport(),
    };
  },
};
